import type {RefObject} from 'react';
import { useEffect, useState} from 'react';

interface ContainerScrollingReturnValue {
  isScrolling: boolean;
  scrollTop: number;
}

const DEFAULT_SCROLL_OFFSET = 300;

/**
 * Hook that returns a ContainerScrollingReturnValue,
 * meant to be used when checking for the scroll position
 * of the given containerRef
 * @param {RefObject<HTMLElement>} containerRef
 * @param isElementMounted
 * @param scrollingOffset
 */
const useIsContainerScrolling = (
  containerRef: RefObject<HTMLElement | null>,
  isElementMounted?: boolean,
  scrollingOffset: number = DEFAULT_SCROLL_OFFSET
): ContainerScrollingReturnValue => {
  const [scrollTop, setScrollTop] = useState<number>(0);

  const handleScroll = (): void => {
    if (containerRef && containerRef.current) {
      setScrollTop(containerRef?.current?.scrollTop);
    }
  };

  useEffect(() => {
    containerRef?.current?.addEventListener('scroll', handleScroll);

    return (): void => {
      containerRef?.current?.removeEventListener('scroll', handleScroll);
    };
  }, [isElementMounted]);

  const object: ContainerScrollingReturnValue = {
    isScrolling: scrollTop > scrollingOffset,
    scrollTop,
  };

  return object;
};

export default useIsContainerScrolling;
